import React, { useContext, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import logoInjupemp from "../../img/LogoInjupemp2026.png";
import { ContextSession } from "../../services/ContextSession";
import {
  apiRequest,
  getDesignacionFirma,
  mapDesignacionSearchToUi,
} from "./enrolamientoApi";
import {
  formatIdentificacionVisual,
  stripIdentificacionVisual,
} from "./identificacionUtils";

const UI_PRIMARY = "#19498c";
const OFICINA_NOMBRES = {
  1: "Tegucigalpa",
  2: "San Pedro Sula",
  3: "La Ceiba",
  4: "Choluteca",
  5: "Santa Rosa de Copán",
  6: "Santa Bárbara",
  7: "Juticalpa",
};

const resolverNombreOficina = (oficinaSesion) => {
  const raw = String(oficinaSesion ?? "").trim();
  if (!raw) return OFICINA_NOMBRES[1];
  return OFICINA_NOMBRES[raw] || OFICINA_NOMBRES[Number(raw)] || raw;
};
const resolverLugarNacimientoReporte = (lugarNacimiento) => {
  const raw = String(lugarNacimiento ?? "").trim();
  return raw;
};

export default function ImprimirDesignacion() {
  const sessi = useContext(ContextSession);
  const authToken = sessi?.sessionData?.token || "";
  const [dni, setDni] = useState("");
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [resultado, setResultado] = useState(null);
  const [firmaDirector, setFirmaDirector] = useState(null);
  const [archivoFirmado, setArchivoFirmado] = useState(null);
  const [ocultarPanelImpresion, setOcultarPanelImpresion] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const designacionSeleccionada = useMemo(() => {
    if (!resultado) return null;
    return (
      resultado.designaciones.find((item) => item.estado === "EN PROCESO") ||
      null
    );
  }, [resultado]);

  const totalActual = useMemo(() => {
    if (!designacionSeleccionada) return 0;
    return designacionSeleccionada.beneficiarios.reduce(
      (acc, item) => acc + Number(item.porcentaje || 0),
      0,
    );
  }, [designacionSeleccionada]);

  const handleBuscar = async () => {
    const cleaned = (dni || "").trim();
    if (!cleaned) {
      setError("Ingrese el DNI para buscar.");
      setOk("");
      setResultado(null);
      setFirmaDirector(null);
      return;
    }
    try {
      setLoading(true);
      const data = await apiRequest(
        `/Enrolamiento/designaciones/buscar/?dni=${encodeURIComponent(cleaned)}&tipo=L`,
        { authToken },
      );
      const found = mapDesignacionSearchToUi(data);
      const enProceso = found.designaciones.find(
        (item) => item.estado === "EN PROCESO",
      );
      const firma = await getDesignacionFirma(authToken).catch(() => null);
      setFirmaDirector(firma);
      if (!enProceso) {
        setResultado(null);
        setArchivoFirmado(null);
        setOcultarPanelImpresion(false);
        setError(
          "No se encontró ninguna designación en estado 'En Proceso' para este DNI.",
        );
        setOk("");
        return;
      }
      setResultado(found);
      setArchivoFirmado(null);
      setOcultarPanelImpresion(false);
      setError("");
      setOk("");
    } catch (err) {
      setError(
        err?.message || "No se encontro registro para el DNI ingresado.",
      );
      setOk("");
      setResultado(null);
      setFirmaDirector(null);
      setArchivoFirmado(null);
      setOcultarPanelImpresion(false);
    } finally {
      setLoading(false);
    }
  };

  const handleLimpiar = () => {
    setDni("");
    setError("");
    setOk("");
    setResultado(null);
    setFirmaDirector(null);
    setArchivoFirmado(null);
    setOcultarPanelImpresion(false);
  };

  const handleArchivoSeleccionado = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !resultado?.personaId) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("archivo", file);
      formData.append("observaciones", "Archivo de ficha firmada");
      formData.append(
        "designacion_id",
        String(designacionSeleccionada.idDesignacion),
      );
      await apiRequest(
        `/Enrolamiento/personas/${encodeURIComponent(resultado.personaId)}/adjuntos/`,
        {
          authToken,
          method: "POST",
          body: formData,
        },
      );
      setArchivoFirmado(file);
      setOcultarPanelImpresion(true);
      setOk("Archivo adjuntado correctamente.");
      setError("");
    } catch (err) {
      setError(err?.message || "No se pudo adjuntar el archivo.");
      setOk("");
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const abrirReporteHtml = () => {
    if (!resultado || !designacionSeleccionada) return;
    const reporteVentana = window.open("", "_blank", "width=960,height=700");
    if (!reporteVentana) return;

    const fechaActual = new Date();
    const _MESES = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];
    const fechaLarga = `${fechaActual.getDate()} días de ${_MESES[fechaActual.getMonth()]} de ${fechaActual.getFullYear()}`;
    const horaBase = fechaActual.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    const [horaSimple, meridiano] = horaBase.split(" ");
    const horaLarga = `${horaSimple} ${meridiano?.toLowerCase() === "am" ? "a.m." : "p.m."}`;
    const numeroActa = String(
      designacionSeleccionada?.numero ?? designacionSeleccionada?.Numero ?? "",
    ).trim();
    const nombreAfiliado =
      [
        resultado?.primerNombre,
        resultado?.segundoNombre,
        resultado?.primerApellido,
        resultado?.segundoApellido,
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim() || String(resultado?.nombreCompleto || "").trim();
    const firmaDirectorImg = firmaDirector?.ImagenDataUrl || "";
    const nombreOficinaFirma = resolverNombreOficina(
      sessi?.sessionData?.oficina,
    );
    const lugarNacimientoReporte = resolverLugarNacimientoReporte(
      resultado?.lugarNacimiento || resultado?.lugarNacimientoReporte,
    );
    const fechaNacPartes = (resultado.fechaNacimiento || "").split("-");
    const fechaNacimientoFormato =
      fechaNacPartes.length === 3
        ? `${fechaNacPartes[2]}-${fechaNacPartes[1]}-${fechaNacPartes[0]}`
        : resultado.fechaNacimiento;

    const filasBeneficiarios = designacionSeleccionada.beneficiarios
      .map(
        (item) => `
          <tr>
            <td>${`${item.primerNombre} ${item.segundoNombre || ""} ${item.primerApellido} ${item.segundoApellido || ""}`.replace(/\s+/g, " ").trim()}</td>
            <td>${formatIdentificacionVisual(item.dni)}</td>
            <td>${item.parentesco || "BENEFICIARIO"}</td>
            <td style="text-align:right;">${item.porcentaje}%</td>
          </tr>`,
      )
      .join("");

    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Constancia de Designacion de Beneficiarios Legales</title>
    <style>
      @page { size: legal portrait; margin: 14mm 14mm; }
      body { font-family: Arial, sans-serif; color: #111; font-size: 12px; margin: 0; background: #fff; }
      .toolbar { text-align: right; margin: 8px auto 10px; width: 7.35in; }
      .sheet { width: 7.35in; min-height: 12.9in; margin: 0 auto; display: flex; flex-direction: column; justify-content: space-between; }
      .main-block { padding-top: 0.08in; }
      .const-block { padding-bottom: 0.08in; }
      .header { display: flex; justify-content: center; align-items: center; margin-top: 0.12in; margin-bottom: 0.22in; }
      .logo { height: 68px; width: auto; object-fit: contain; }
      .title-row { display: flex; justify-content: space-between; align-items: baseline; margin: 0 0 0.16in; }
      .title-main { font-size: 13px; font-weight: 700; letter-spacing: .2px; }
      .title-num { font-size: 13px; font-weight: 700; }
      p { margin: 0 0 0.11in; line-height: 1.35; text-align: justify; }
      table { width: 100%; border-collapse: collapse; margin-top: 0.14in; margin-bottom: 0.2in; }
      th, td { border: 1px solid #777; padding: 4px 6px; font-size: 10.5px; }
      th { background: #AD8401; text-align: center; font-weight: 700; }
      .center { text-align: center; }
      .small-note { font-size: 9px; margin-top: 0.08in; }
      .firma-wrap { margin-top: 0.8in; text-align: center; }
      .firma-participante { margin-top: 0.1in; text-align: center; }
      .firma-line { border-top: 1px solid #000; width: 220px; margin: 0 auto 4px; }
      .firma-director-signature { min-height: 0.1in; margin: 0 auto 2px; display: flex; align-items: flex-end; justify-content: center; }
      .firma-director-signature img { width: 260px; max-width: 260px; max-height: 1.25in; object-fit: contain; }
      .firma-label { font-size: 9px; font-weight: 700; }
      .huella-space { position: relative; height: 1.05in; }
      .huella-mark { position: absolute; right: 1.45in; bottom: 0.03in; width: 0.52in; height: 0.74in; border: 1px dashed #8a8a8a; border-radius: 50% 50% 45% 45%; opacity: .45; }
      .divider { border-top: 1px dashed #666; margin: 0.16in 0 0.12in; }
      .const-title { display: flex; justify-content: space-between; font-size: 12px; font-weight: 700; margin-bottom: 0.1in; }
      .btn-print { background: #0c9a8e; color: #fff; border: 0; border-radius: 4px; padding: 7px 14px; font-size: 12px; cursor: pointer; }
      @media print { .toolbar { display: none; } body { margin: 0; } }
    </style>
  </head>
  <body>
    <div class="toolbar"><button class="btn-print" onclick="window.print()">Imprimir</button></div>
    <div class="sheet">
    <div class="main-block">
      <div class="header"><img class="logo" src="${logoInjupemp}" alt="INJUPEMP" /></div>
      <div class="title-row">
        <div class="title-main">SE DESIGNAN BENEFICIARIOS LEGALES</div>
        <div class="title-num">N° ${numeroActa}</div>
      </div>
      <p>Señor Director Especialista Presidente del Instituto Nacional de Jubilaciones y Pensiones de los Empleados y Funcionarios del Poder Ejecutivo (INJUPEMP):</p>
      <p>Yo <strong>${nombreAfiliado}</strong>, con tarjeta de identidad N° <strong>${formatIdentificacionVisual(resultado.dni)}</strong>, nacido en: <strong>${lugarNacimientoReporte || "-"}</strong>, el <strong>${fechaNacimientoFormato}</strong>. De Conformidad con los artículos 86, 87 y 89 de la Ley del Instituto Nacional de Jubilaciones y Pensiones de los Empleados y Funcionarios del poder Ejecutivo(INJUPEMP), y para los efectos Jurídicos Correspondientes Comparezco ante Usted a manifestar de forma libre y espontanea la designación de mis beneficiarios legales, los que acontinuación detallo:</p>
      <table>
        <thead>
          <tr>
            <th>Nombres y Apellidos</th>
            <th>No de identidad</th>
            <th>Parentesco / Otro</th>
            <th>Porcentaje</th>
          </tr>
        </thead>
        <tbody>
          ${filasBeneficiarios}
          <tr>
            <td colspan="3" style="text-align:right;font-weight:700;">TOTAL</td>
            <td style="text-align:right;font-weight:700;">${totalActual}%</td>
          </tr>
        </tbody>
      </table>
      <p>La anterior designación la ratifico por ser mi expresa voluntad y así mismo dispongo que si alguno de los beneficiarios descritos falleciere, respecto al porcentaje a él asignado se esté a lo que dispone el artículo 89 de la ley de Instituto Nacional de Jubilaciones y Pensiones y funcionarios del Poder Ejecutivo (INJUPEMP) Me reservo el derecho de revocar o modificar la presente Designación de Beneficiarios Legales.</p>
      <p>Firmo en ${nombreOficinaFirma}, a los ${fechaLarga}, siendo las ${horaLarga}.</p>
      <div class="firma-participante">
        <div class="huella-space"><div class="huella-mark"></div></div>
        <div class="firma-line"></div>
        <div class="firma-label">FIRMA Y HUELLA</div>
      </div>
      <p class="small-note center">Nota: Asegúrese de escribir el nombre de los beneficiarios designados conforme aparece la certificación de acta de nacimiento o tarjeta de identidad respectiva y que los porcentajes distribuidos entre los beneficiarios designados, totalice el 100%.</p>
      <div class="firma-wrap">
        ${
          firmaDirectorImg
            ? `<div class="firma-director-signature"><img src="${firmaDirectorImg}" alt="Firma del Director Especialista Presidente" onerror="this.style.display='none';" /></div>`
            : `<div class="firma-director-signature"></div>`
        }
        <div class="firma-line"></div>
        <div class="firma-label">FIRMA Y SELLO DIRECTOR ESPECIALISTA PRESIDENTE</div>
      </div>
    </div>
    <div class="const-block">
      <div class="divider"></div>
      <div class="const-title">
        <span>CONSTANCIA DE DESIGNACIÓN BENEFICIARIOS LEGALES</span>
        <span>N° ${numeroActa}</span>
      </div>
      <p>El señor (a) <strong>${nombreAfiliado}</strong> ha presentado en esta fecha su designación de beneficiarios legales en el Instituto Nacional de Jubilaciones y Pensiones de los Empleados y Funcionarios del Poder Ejecutivo (INJUPEMP).</p>
      <p>Firmo en ${nombreOficinaFirma}, a los ${fechaLarga}, hora ${horaLarga}.</p>
      <div class="firma-wrap">
        <div class="firma-line"></div>
        <div class="firma-label">FIRMA Y SELLO ENCARGADO</div>
      </div>
    </div>
    </div>
  </body>
</html>`;

    reporteVentana.document.open();
    reporteVentana.document.write(html);
    reporteVentana.document.close();
  };

  return (
    <Box
      sx={{
        p: { xs: 1.5, md: 2.4 },
        background:
          "linear-gradient(180deg, rgba(25,73,140,0.08) 0%, rgba(173,132,241,0.09) 100%)",
        minHeight: "calc(100vh - 90px)",
      }}
    >
      <Paper
        sx={{
          p: { xs: 1.6, md: 2.2 },
          border: "1px solid #dbe6f6",
          borderRadius: 3,
          boxShadow: "0 12px 30px rgba(25,73,140,0.12)",
        }}
      >
        <Typography sx={{ fontWeight: 700, mb: 1.5 }}>
          Designacion de Beneficiarios Legales
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr auto auto" },
            gap: 1.2,
            alignItems: "end",
          }}
        >
          <TextField
            label="DNI"
            size="small"
            value={formatIdentificacionVisual(dni)}
            onChange={(event) =>
              setDni(stripIdentificacionVisual(event.target.value))
            }
            inputProps={{ maxLength: 15 }}
          />
          <Button
            variant="contained"
            startIcon={<SearchIcon />}
            onClick={handleBuscar}
            disabled={loading}
            sx={{
              backgroundColor: UI_PRIMARY,
              color: "#fff",
              borderRadius: 2.5,
              px: 2.2,
            }}
          >
            {loading ? "Buscando..." : "Buscar"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleLimpiar}
            sx={{
              borderColor: UI_PRIMARY,
              color: UI_PRIMARY,
              borderRadius: 2.5,
            }}
          >
            Limpiar
          </Button>
        </Box>
        {error && (
          <Alert severity="warning" sx={{ mt: 1.2 }}>
            {error}
          </Alert>
        )}
        {ok && (
          <Alert severity="success" sx={{ mt: 1.2 }}>
            {ok}
          </Alert>
        )}
      </Paper>

      {resultado && designacionSeleccionada && !ocultarPanelImpresion && (
        <Paper
          sx={{
            p: { xs: 1.6, md: 2.2 },
            mt: 1.8,
            border: "1px solid #dbe6f6",
            borderRadius: 3,
            boxShadow: "0 12px 30px rgba(25,73,140,0.12)",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "auto 1fr auto" },
              gap: 1,
              alignItems: "center",
            }}
          >
            <Button
              startIcon={<PrintIcon />}
              variant="outlined"
              onClick={abrirReporteHtml}
              sx={{
                borderColor: UI_PRIMARY,
                color: UI_PRIMARY,
                borderRadius: 1.5,
              }}
            >
              Imprimir
            </Button>
            <TextField
              size="small"
              value={archivoFirmado?.name || ""}
              placeholder="Archivo de ficha firmada"
              InputProps={{ readOnly: true }}
            />
            <Button
              startIcon={<AttachFileIcon />}
              variant="outlined"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              sx={{
                borderColor: UI_PRIMARY,
                color: UI_PRIMARY,
                borderRadius: 1.5,
              }}
            >
              {uploading ? "Adjuntando..." : "Adjuntar"}
            </Button>
            <input
              ref={inputRef}
              type="file"
              hidden
              onChange={handleArchivoSeleccionado}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
}
